import { useMemo } from 'react';
import { ExpenseFormDataQuery } from '../../../../__generated__/graphql';
import { OptionBase } from '../../../../../../components/Select/types';

export type UseExpenseDropdownOptionsReturn = {
  expenseGroupOptions: OptionBase[];
  projectOptions: OptionBase[];
  categoryOptions: OptionBase[];
  merchantOptions: OptionBase[];
};

export const useExpenseDropdownOptions = (
  customMerchants: OptionBase[] = [],
  expenseFormData?: ExpenseFormDataQuery,
): UseExpenseDropdownOptionsReturn => {
  const expenseGroupOptions = useMemo<OptionBase[]>(
    () =>
      expenseFormData?.me?.myExpenseGroups
        ?.filter((group) => group.flair__Status__c === 'DRAFT')
        .map((group) => ({
          value: group.Id,
          label: group.Name,
        })) || [],
    [expenseFormData],
  );

  const projectOptions = useMemo<OptionBase[]>(
    () =>
      expenseFormData?.projects?.map((project) => ({
        value: project.Id,
        label: project.flair__Name__c,
      })) || [],
    [expenseFormData],
  );

  const categoryOptions = useMemo<OptionBase[]>(
    () =>
      expenseFormData?.expenseCategories?.map((category) => ({
        value: category.Id,
        label: category.Name,
      })) || [],
    [expenseFormData],
  );

  const merchantOptions = useMemo<OptionBase[]>(() => {
    const apiMerchants =
      expenseFormData?.merchants?.map((merchant) => ({
        label: merchant.Name || '',
        value: merchant.Id,
      })) || [];

    const merchantMap = new Map<string, OptionBase>();

    apiMerchants.forEach((merchant) => {
      const key = merchant.value;
      if (!merchantMap.has(key)) {
        merchantMap.set(key, merchant);
      }
    });

    customMerchants.forEach((merchant) => {
      const valueKey = merchant.value;
      const labelKey = merchant.label;
      const existsByValue = merchantMap.has(valueKey);
      const existsByLabel = Array.from(merchantMap.values()).some(
        (m) => m.label === labelKey,
      );
      if (!existsByValue && !existsByLabel) {
        merchantMap.set(valueKey, merchant);
      }
    });

    return Array.from(merchantMap.values());
  }, [expenseFormData?.merchants, customMerchants]);

  return {
    expenseGroupOptions,
    projectOptions,
    categoryOptions,
    merchantOptions,
  };
};
